import { notification } from 'antd'
import i18next from 'i18next'
import { makeAutoObservable } from 'mobx'

import { FileEntityType, fileService } from 'services/FileService'
import { ConvertedFile } from 'utils/helpers/files/fileUpload'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { PHOTO_FORMATS, VIDEO_FORMATS } from 'shared/constants/values'

import { ticketApi } from '../../api'
import { TicketKeys } from '../../data'
import { TicketFileUploadDTO, TicketStatus } from '../../types'
import { ViewTicketUiStore } from './ViewTicketUiStore'

const getKey = (key: string) => `${TicketKeys.ticketModule}.api.${key}`

export class ViewTicketApiStore {
  constructor(protected readonly uiStore: ViewTicketUiStore) {
    makeAutoObservable(this)
  }

  async createSupportLog() {
    const ticketId = this.uiStore.selectedTicket?.id

    if (!ticketId) {
      return
    }

    const photos = await this.fileUpload(
      this.uiStore.files.filter((file) => PHOTO_FORMATS.includes(file.format.toLowerCase())),
    )
    const videos = await this.fileUpload(
      this.uiStore.files.filter((file) => VIDEO_FORMATS.includes(file.format.toLowerCase())),
    )

    const files = [...photos, ...videos]

    if (files.length < this.uiStore.files.length || files.some((file) => file?.url === '')) {
      notification.error({
        message: i18next.t(getSharedKey('api.filesUploadError')),
      })

      return false
    }

    try {
      await ticketApi.createSupportLog({
        message: this.uiStore.message.trim(),
        ticketId,
        files: {
          image_urls: photos.map((file) => file?.url ?? ''),
          video_urls: videos.map((file) => file?.url ?? ''),
          document_urls: [],
        },
      })

      await this.uiStore.getTicketList(true)
      this.uiStore.setSelectedTicket(this.uiStore.getTicket(ticketId))

      return true
    } catch (error) {
      notification.error({
        message: i18next.t(getSharedKey('api.sendMessageError')),
        description: (error as Error).message,
      })
    }
  }

  async fileUpload(files: ConvertedFile[]) {
    try {
      const promiseFiles: Promise<TicketFileUploadDTO | null>[] = []

      for (const file of files) {
        /* mytodo: поменять после выкатки замков */
        const res = fileService.ticketFileUpload({
          totalSize: file.fileSize,
          content: file.content,
          entityType: FileEntityType.ticket,
        })

        promiseFiles.push(res)
      }

      return await Promise.all(promiseFiles)
    } catch (error) {
      notification.error({
        message: i18next.t(getSharedKey('api.filesUploadError')),
        description: (error as Error).message,
      })

      return []
    }
  }

  async closeTicket() {
    const ticketId = this.uiStore.selectedTicket?.id

    if (!ticketId) {
      return
    }

    try {
      await ticketApi.closeTicket(ticketId)

      this.uiStore.ticketStore.patchEntity(ticketId, {
        status: TicketStatus.closed,
        finishDate: new Date().toISOString(),
      })
      this.uiStore.setSelectedTicket(this.uiStore.getTicket(ticketId), true)

      return true
    } catch (error) {
      notification.error({ message: i18next.t(getKey('closeTicketError')), description: (error as Error).message })
    }
  }

  async rateTicket(grade: number, message: string) {
    const ticketId = this.uiStore.selectedTicket?.id

    if (!ticketId) {
      return
    }

    try {
      await ticketApi.rateTicket({ ticketId, grade, message })

      this.uiStore.ticketStore.patchEntity(ticketId, {
        ticketGrade: grade,
        gradeComment: message,
      })

      notification.success({ message: i18next.t(getKey('rateTicketSuccess')) })

      return true
    } catch (error) {
      notification.error({ message: i18next.t(getKey('rateTicketError')), description: (error as Error).message })
    }
  }
}
